import { unsafeCSS, html, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import { PopBaseComponent } from '../../../base/pop-base-component';
import {
  Product,
  SortDirection,
  TableRecordVariant,
  type CurrentSort,
  type IPCT,
  PerformanceColumn,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants';
import { Constants } from './performance-table.constants';
import { customFormatDate } from '../../../common/utils/formatDate';
import {
  abbreviateNumberToCurrencyWithCodeSuffix,
  formatToNDecimalPlaces,
} from '../../../common/utils/formatNumber';
import { getCurrencySymbol } from '../../../common/utils/currency';
import { buildShareClassDescriptor } from '../../../common/utils/buildShareClass';
import { zustandStateToComponent } from '../../store/product-center.store';
import { TableTypeEnum } from '../../store/product-center.store.model';
import {
  paginateTable,
  renderPagination,
  sortDataCells,
  sortProductDataCells,
  validateIfProductHeaderSort,
} from '../common/helper/table-helper';
import {
  checkProductCallback,
  getAPCDateFormat,
  getProductIdentifier,
  getProductIdentifierLabel,
  handleAdvancedSortByDropdownChange,
  handleSortProductDataCells,
  handleUpdate,
  renderProductCellData,
  renderProductHeader,
  renderTableCells,
  renderTableHeader,
  sortDataCellsCallBack,
} from '../../product-center-table-wrapper/product-center-table-wrapper.utils';
import Locale from '../../../common/utils/locale.js';
import { PopConfiguration } from '../../../common/configuration';

import style from './performance-table.scss?inline';

const MORNINGSTAR_MAX_RATING = 5;

/**
 * @tag pop-pc-performance-table-component
 * @property {ProductCenterExhibit} [componentObject] Performance Table content
 * @attribute {ProductCenterExhibit} [data-component-object] Performance Table content
 */
export class PopPerformanceTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @property()
  private readonly ipct: IPCT = {};

  /**
   * @internal
   */
  @property()
  private filteredProducts: Product[] = [];

  /**
   * @internal
   */
  @property()
  private sortByDropdownValue =
    ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;

  /**
   * @internal
   */
  @property()
  selectedProducts: Product['productCode'][] = [];

  @state()
  public sortDirectionState: SortDirection = SortDirection.ASCENDING;

  @property()
  tableHeaders: NodeListOf<Element> | undefined;

  @query('#performance-pagination-table')
  performancePaginationTable!: HTMLElement;

  @query('#performance-pagination')
  performancePagination!: HTMLElement;

  protected popConfiguration: PopConfiguration = new PopConfiguration();

  private currentSort?: CurrentSort;

  /*
   * Component Methods
   */

  constructor() {
    super();
    this.enableI18n = true;
  }

  update(changedProperties: Map<string, unknown>) {
    handleUpdate(this, changedProperties);
    super.update(changedProperties);
  }

  private hasColumn(performanceColumn: PerformanceColumn) {
    return this.ipct?.configuration?.tabs.performance.includes(
      performanceColumn,
    );
  }

  private sortProductDataCells(e: CustomEvent) {
    handleSortProductDataCells(this, e);
  }

  private sortByDropdownChangeHandler(e: CustomEvent) {
    this.sortByDropdownValue = handleAdvancedSortByDropdownChange(
      e,
      ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value,
      this.tableHeaders,
      this.filteredProducts,
      ProductCenterTableWrapperConstants.SORT_BUTTON_ID,
      this.shadowRoot,
      this.requestUpdate.bind(this),
      (sort) => {
        this.currentSort = sort;
      },
      (products) => {
        this.filteredProducts = products;
      },
    );
    if (this.currentSort) {
      this.currentSort.columnId = undefined;
    }
  }

  private getPerformanceAsOfDate(): string {
    let asOfDate = '';

    if (this.filteredProducts) {
      for (const product of this.filteredProducts) {
        if (product.performance && product.performance.performanceAsOfDate) {
          asOfDate = product.performance.performanceAsOfDate;
          break;
        }
      }
    }

    return asOfDate;
  }

  private getMorningstarAsOfDate(): string {
    let asOfDate = '';

    if (this.filteredProducts) {
      for (const product of this.filteredProducts) {
        if (product.performance && product.performance.morningstarAsOfDate) {
          asOfDate = product.performance.morningstarAsOfDate;
          break;
        }
      }
    }

    return asOfDate;
  }

  private renderTableHead() {
    return html`
      <tr>
        ${renderProductHeader(
          {
            rowspan: 2,
            sortByDropdownValue: this.sortByDropdownValue,
            sortByCallBack: this.sortByDropdownChangeHandler,
            onSort: this.sortProductDataCells,
            tableType: TableTypeEnum.PERFORMANCE,
          },
          new Locale(this.language, this.country),
        )}
        <th
          scope="col"
          colspan="6"
          id="${Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE}"
        >
          <div class="b-flex b-justify-between pop-performance-header">
            <span class="pop-performance-title"
              >${this.localize(Constants.TRANSLATION_KEYS.HEADERS.TABLE_NAME)}
              %</span
            >
            <span class="b-typography-sans-serif--caption--100"
              >${this.localize('as_of_date', {
                date: this.getPerformanceAsOfDate(),
              })}</span
            >
          </div>
        </th>
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.INCEPTION_DATE,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.INCEPTION_DATE,
          ),
          showAnnualised: false,
          colspan: 1,
          rowspan: 2,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.INCEPTION_DATE,
            ),
        })}
        ${!this.hasColumn(PerformanceColumn.FUND_SIZE)
          ? ''
          : renderTableHeader({
              columnId: Constants.PERFORMANCE_COLUMN_IDS.FUND_SIZE,
              headerName: this.localize(
                Constants.TRANSLATION_KEYS.HEADERS.FUND_SIZE,
              ),
              showAnnualised: false,
              colspan: 1,
              rowspan: 2,
              parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
              sortable: true,
              onSort: (e: CustomEvent) =>
                sortDataCellsCallBack(
                  this,
                  e,
                  Constants.PERFORMANCE_COLUMN_IDS.FUND_SIZE,
                ),
            })}
        ${!this.hasColumn(PerformanceColumn.MORNINGSTAR_RATING)
          ? ''
          : html`<th scope="col" colspan="2">
              <div class="b-flex b-justify-between pop-performance-header">
                <span class="pop-performance-title"
                  >${this.localize(
                    Constants.TRANSLATION_KEYS.HEADERS.MORNINGSTAR,
                  )}</span
                >
                <span class="b-typography-sans-serif--caption--100"
                  >${this.localize('as_of_date', {
                    date: this.getMorningstarAsOfDate(),
                  })}</span
                >
              </div>
            </th>`}
      </tr>
      <tr>
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.YTD,
          headerName: this.localize(Constants.TRANSLATION_KEYS.HEADERS.YTD),
          showAnnualised: false,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.YTD,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.ONE_YEAR,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.ONE_YEAR,
          ),
          showAnnualised: false,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.ONE_YEAR,
            ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.THREE_YEARS,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.THREE_YEARS,
          ),
          showAnnualised: true,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.THREE_YEARS,
            ),
          annualised: this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.ANNUALISED,
          ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.FIVE_YEARS,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.FIVE_YEARS,
          ),
          showAnnualised: true,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.FIVE_YEARS,
            ),
          annualised: this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.ANNUALISED,
          ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.TEN_YEARS,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.TEN_YEARS,
          ),
          showAnnualised: true,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.TEN_YEARS,
            ),
          annualised: this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.ANNUALISED,
          ),
        })}
        ${renderTableHeader({
          columnId: Constants.PERFORMANCE_COLUMN_IDS.SINCE_INCEPTION,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.HEADERS.SINCE_INCEPTION,
          ),
          showAnnualised: true,
          colspan: 1,
          rowspan: 1,
          parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
          sortable: true,
          onSort: (e: CustomEvent) =>
            sortDataCellsCallBack(
              this,
              e,
              Constants.PERFORMANCE_COLUMN_IDS.SINCE_INCEPTION,
            ),
          annualised: this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.ANNUALISED,
          ),
        })}
        ${!this.hasColumn(PerformanceColumn.MORNINGSTAR_RATING)
          ? ''
          : renderTableHeader({
              columnId: Constants.PERFORMANCE_COLUMN_IDS.MORNINGSTAR_MEDALIST,
              headerName: this.localize(
                Constants.TRANSLATION_KEYS.HEADERS.MORNINGSTAR_MEDALIST,
              ),
              showAnnualised: false,
              colspan: 1,
              rowspan: 2,
              parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
              sortable: true,
              onSort: (e: CustomEvent) =>
                sortDataCellsCallBack(
                  this,
                  e,
                  Constants.PERFORMANCE_COLUMN_IDS.MORNINGSTAR_MEDALIST,
                ),
            })}
        ${!this.hasColumn(PerformanceColumn.MORNINGSTAR_RATING)
          ? ''
          : renderTableHeader({
              columnId: Constants.PERFORMANCE_COLUMN_IDS.MORNINGSTAR_RATING,
              headerName: this.localize(
                Constants.TRANSLATION_KEYS.HEADERS.MORNINGSTAR_RATING,
              ),
              showAnnualised: false,
              colspan: 1,
              rowspan: 2,
              parentHeader: Constants.PERFORMANCE_COLUMN_IDS.PERFORMANCE_TABLE,
              sortable: true,
              onSort: (e: CustomEvent) =>
                sortDataCellsCallBack(
                  this,
                  e,
                  Constants.PERFORMANCE_COLUMN_IDS.MORNINGSTAR_RATING,
                ),
            })}
      </tr>
    `;
  }

  private renderTableBody() {
    if (this.currentSort) {
      if (validateIfProductHeaderSort(this.currentSort.columnId)) {
        this.filteredProducts = sortProductDataCells(
          this.currentSort.tableHeaderEl,
          this.filteredProducts,
          this.currentSort.columnId,
        );
      } else {
        this.filteredProducts = sortDataCells(
          this.currentSort.tableHeaderEl,
          this.filteredProducts,
          this.currentSort.columnId,
        );
      }
    } else {
      // Apply default sorting by shareClass when initializing
      this.filteredProducts = sortProductDataCells(
        undefined,
        this.filteredProducts,
        ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION
          .value,
      );
    }

    return html`
      ${this.filteredProducts.map((product: Product) => {
        return html`
          <tr>
            ${renderProductCellData({
              index: JSON.stringify(product.productName),
              sortValue: product.productName[this.sortByDropdownValue] ?? '',
              checkboxLabel: product.productName.vehicleTypeName,
              fundName: product.productName.fundName,
              shareClass: product.productName.shareClass
                ? buildShareClassDescriptor(product.productName.shareClass)
                : '',
              currencySymbol: getCurrencySymbol(
                product.productName.baseCurrencyName,
              ),
              currencyName: product.productName.baseCurrencyName,
              productIdentifier: getProductIdentifier(product, this.ipct),
              productCode: product.productCode,
              checkProductCallBack: (event: Event) =>
                checkProductCallback(event, TableTypeEnum.PERFORMANCE),
              isChecked: this.selectedProducts.includes(product.productCode),
              fundLink: product.productName.fundLink,
              showPopCheckbox: true,
              disclaimers: product.disclaimers ?? undefined,
              productIdentifierLabel: this.localize(
                getProductIdentifierLabel(product, this.ipct),
              ),
              noticeLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.NOTICE,
              ),
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.yearToDate?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.yearToDate?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.oneYear?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.oneYear?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.threeYears?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.threeYears?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.fiveYears?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.fiveYears?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${product.performance?.fundReturns?.tenYears?.[0]?.value}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.tenYears?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${
                product.performance?.fundReturns?.sinceInception?.[0]?.value
              }`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${formatToNDecimalPlaces(
                product.performance?.fundReturns?.sinceInception?.[0]?.value,
                2,
                ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
              )}`,
            })}
            ${renderTableCells({
              sortValue: `${+new Date(product.performance?.inceptionDate ?? '')}`,
              variant: TableRecordVariant.NUMERAL,
              cellClass: '',
              value: html`${getAPCDateFormat(
                customFormatDate(
                  product.performance?.inceptionDate,
                  ProductCenterTableWrapperConstants.DATE_FORMATS
                    .MEDIUM_DATE_FORMAT,
                ),
              )}`,
            })}
            ${!this.hasColumn(PerformanceColumn.FUND_SIZE)
              ? ''
              : renderTableCells({
                  sortValue: `${+new Date(product.performance?.fundSize ?? '')}`,
                  variant: TableRecordVariant.NUMERAL,
                  cellClass: '',
                  value: html`${abbreviateNumberToCurrencyWithCodeSuffix(
                    product.performance?.fundSize,
                    product.productName.reportingCurrency,
                    2,
                    this.locale,
                    ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL,
                  )}`,
                })}
            ${!this.hasColumn(PerformanceColumn.MORNINGSTAR_RATING)
              ? ''
              : renderTableCells({
                  sortValue: product.performance?.morningstarMedalistRating,
                  variant: TableRecordVariant.TEXT,
                  cellClass: Constants.RATING_ROW_CLASS,
                  value: product.performance?.morningstarMedalistRating
                    ? html`<img
                        class="pop-rating-image"
                        src="${this.popConfiguration.getAssetBaseUrl()}/images/${product
                          .performance?.morningstarMedalistRating}.png"
                      />`
                    : html`${ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL}`,
                })}
            ${!this.hasColumn(PerformanceColumn.MORNINGSTAR_RATING)
              ? ''
              : renderTableCells({
                  sortValue: product.performance?.morningstarRating?.toString(),
                  variant: TableRecordVariant.NUMERAL,
                  cellClass: Constants.RATING_ROW_CLASS,
                  value: product.performance?.morningstarRating
                    ? html`${Array(MORNINGSTAR_MAX_RATING)
                        .fill(0)
                        .map((_, i) => {
                          const colorClass =
                            i < product.performance?.morningstarRating
                              ? 'pop-filled'
                              : 'pop-empty';
                          return html`<span
                            class="pop-morningstar ${colorClass}"
                            >&#9733;</span
                          >`;
                        })} `
                    : html`${ProductCenterTableWrapperConstants.CUSTOM_NO_VALUE_SYMBOL}`,
                })}
          </tr>
        `;
      })}
    `;
  }

  private applyPagination() {
    paginateTable(this.performancePaginationTable, this.performancePagination);
  }

  renderComponent() {
    return html` <beacon-table
        variant="fixed"
        density="standard"
        id="${ProductCenterTableWrapperConstants.TABLE_FIELD
          .PERFORMANCE}-pagination-table"
        @beacon-event-element-ready="${this.applyPagination}"
      >
        <table>
          <thead>
            ${this.renderTableHead()}
          </thead>
          <tbody>
            ${this.renderTableBody()}
          </tbody>
        </table>
      </beacon-table>
      ${this.filteredProducts.length >
      ProductCenterTableWrapperConstants.PAGINATION_TABLE.DEFAULT_PAGE_SIZE
        ? renderPagination(
            this.filteredProducts,
            ProductCenterTableWrapperConstants.TABLE_FIELD.PERFORMANCE,
            this.applyPagination,
          )
        : nothing}`;
  }

  render() {
    if (
      (!this.filteredProducts && !this.componentObject) ||
      !this.isI18nLoaded
    ) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          ipct: productCenterState.originalProductCenterObject.ipct,
          filteredProducts: productCenterState.getFilteredProducts(),
          selectedProducts:
            productCenterState.selectedProducts[TableTypeEnum.PERFORMANCE],
        };
      },
    },
    PopPerformanceTableComponent,
  ),
);
